import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, updateDoc, deleteDoc, addDoc, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import './ScheduledOrders.css';

const ScheduledOrders = () => {
  const [loading, setLoading] = useState(true);
  const [scheduledOrders, setScheduledOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, confirmed, cancelled
  const [filterDate, setFilterDate] = useState('all'); // all, today, tomorrow, week, month
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    cancelled: 0,
    today: 0,
    tomorrow: 0,
    totalRevenue: 0
  });

  useEffect(() => {
    fetchScheduledOrders();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [scheduledOrders, filterStatus, filterDate, searchTerm]);

  const fetchScheduledOrders = async () => {
    try {
      setLoading(true);

      // Fetch all scheduled orders
      const ordersQuery = query(
        collection(db, 'scheduled_orders'),
        orderBy('scheduledDate', 'asc')
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersData = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scheduledDate: doc.data().scheduledDate?.toDate?.() || new Date(doc.data().scheduledDate),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt)
      }));

      setScheduledOrders(ordersData);

      // Calculate statistics
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const todayOrders = ordersData.filter(o => {
        const orderDate = new Date(o.scheduledDate);
        orderDate.setHours(0, 0, 0, 0);
        return orderDate.getTime() === today.getTime() && o.status !== 'cancelled';
      });

      const tomorrowOrders = ordersData.filter(o => {
        const orderDate = new Date(o.scheduledDate);
        orderDate.setHours(0, 0, 0, 0);
        return orderDate.getTime() === tomorrow.getTime() && o.status !== 'cancelled';
      });

      const totalRevenue = ordersData
        .filter(o => o.status !== 'cancelled')
        .reduce((sum, o) => sum + (o.total || 0), 0);

      setStats({
        total: ordersData.length,
        pending: ordersData.filter(o => o.status === 'pending').length,
        confirmed: ordersData.filter(o => o.status === 'confirmed').length,
        cancelled: ordersData.filter(o => o.status === 'cancelled').length,
        today: todayOrders.length,
        tomorrow: tomorrowOrders.length,
        totalRevenue
      });

      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching scheduled orders:', error);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...scheduledOrders];

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(o => o.status === filterStatus);
    }

    // Filter by date
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    if (filterDate === 'today') {
      filtered = filtered.filter(o => {
        const orderDate = new Date(o.scheduledDate);
        orderDate.setHours(0, 0, 0, 0);
        return orderDate.getTime() === today.getTime();
      });
    } else if (filterDate === 'tomorrow') {
      filtered = filtered.filter(o => {
        const orderDate = new Date(o.scheduledDate);
        orderDate.setHours(0, 0, 0, 0);
        return orderDate.getTime() === tomorrow.getTime();
      });
    } else if (filterDate === 'week') {
      filtered = filtered.filter(o => {
        const orderDate = new Date(o.scheduledDate);
        return orderDate >= today && orderDate <= nextWeek;
      });
    } else if (filterDate === 'month') {
      filtered = filtered.filter(o => {
        const orderDate = new Date(o.scheduledDate);
        return orderDate >= today && orderDate <= nextMonth;
      });
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(o =>
        o.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        o.customerPhone?.includes(searchTerm) ||
        o.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredOrders(filtered);
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await updateDoc(doc(db, 'scheduled_orders', orderId), {
        status: newStatus,
        updatedAt: new Date()
      });

      alert(`Order ${newStatus} successfully!`);
      fetchScheduledOrders();
    } catch (error) {
      console.error('❌ Error updating order status:', error);
      alert('Failed to update order status');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this scheduled order?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'scheduled_orders', orderId));
      alert('Order deleted successfully!');
      fetchScheduledOrders();
      setSelectedOrder(null);
    } catch (error) {
      console.error('❌ Error deleting order:', error);
      alert('Failed to delete order');
    }
  };

  const handleConvertToRegularOrder = async (order) => {
    if (!window.confirm('Convert this to a regular order and place it now?')) {
      return;
    }

    try {
      // Add to regular orders collection
      const ordersRef = collection(db, 'orders');
      await addDoc(ordersRef, {
        ...order,
        createdAt: new Date(),
        status: 'pending',
        isScheduled: false,
        originalScheduledDate: order.scheduledDate
      });

      // Remove from scheduled orders
      await deleteDoc(doc(db, 'scheduled_orders', order.id));

      alert('Order converted and placed successfully!');
      fetchScheduledOrders();
      setSelectedOrder(null);
    } catch (error) {
      console.error('❌ Error converting order:', error);
      alert('Failed to convert order');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'confirmed': return '#10b981';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getTimeUntilScheduled = (scheduledDate) => {
    const now = new Date();
    const diff = scheduledDate - now;

    if (diff < 0) return 'Past due';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="scheduled-orders-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading scheduled orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="scheduled-orders-container">
      <div className="scheduled-header">
        <div>
          <h1>📅 Scheduled Orders</h1>
          <p>Manage and track all future orders</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📦</div>
          <div className="stat-info">
            <div className="stat-label">Total Scheduled</div>
            <div className="stat-value">{stats.total}</div>
          </div>
        </div>
        <div className="stat-card pending">
          <div className="stat-icon">⏳</div>
          <div className="stat-info">
            <div className="stat-label">Pending</div>
            <div className="stat-value">{stats.pending}</div>
          </div>
        </div>
        <div className="stat-card confirmed">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <div className="stat-label">Confirmed</div>
            <div className="stat-value">{stats.confirmed}</div>
          </div>
        </div>
        <div className="stat-card cancelled">
          <div className="stat-icon">❌</div>
          <div className="stat-info">
            <div className="stat-label">Cancelled</div>
            <div className="stat-value">{stats.cancelled}</div>
          </div>
        </div>
        <div className="stat-card highlight">
          <div className="stat-icon">🔥</div>
          <div className="stat-info">
            <div className="stat-label">Today</div>
            <div className="stat-value">{stats.today}</div>
          </div>
        </div>
        <div className="stat-card highlight">
          <div className="stat-icon">📆</div>
          <div className="stat-info">
            <div className="stat-label">Tomorrow</div>
            <div className="stat-value">{stats.tomorrow}</div>
          </div>
        </div>
        <div className="stat-card revenue">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <div className="stat-label">Total Revenue</div>
            <div className="stat-value">₹{stats.totalRevenue.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Status</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Date Range</label>
          <select value={filterDate} onChange={(e) => setFilterDate(e.target.value)}>
            <option value="all">All Dates</option>
            <option value="today">Today</option>
            <option value="tomorrow">Tomorrow</option>
            <option value="week">Next 7 Days</option>
            <option value="month">Next 30 Days</option>
          </select>
        </div>

        <div className="filter-group search">
          <label>Search</label>
          <input
            type="text"
            placeholder="Search by name, phone, or order number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-info">
          Showing {filteredOrders.length} of {scheduledOrders.length} orders
        </div>
      </div>

      {/* Orders Grid */}
      <div className="orders-grid">
        {filteredOrders.length === 0 ? (
          <div className="no-orders">
            <div className="no-orders-icon">📭</div>
            <h3>No scheduled orders found</h3>
            <p>No orders match your current filters</p>
          </div>
        ) : (
          filteredOrders.map(order => (
            <div
              key={order.id}
              className={`order-card ${order.status}`}
              onClick={() => setSelectedOrder(order)}
            >
              <div className="order-header">
                <span className="order-number">#{order.orderNumber}</span>
                <span
                  className="order-status"
                  style={{ backgroundColor: getStatusColor(order.status) }}
                >
                  {order.status}
                </span>
              </div>

              <div className="order-customer">
                <strong>{order.customerName}</strong>
                <span>{order.customerPhone}</span>
              </div>

              <div className="order-schedule">
                <div className="schedule-date">
                  📅 {order.scheduledDate.toLocaleDateString()}
                </div>
                <div className="schedule-time">
                  ⏰ {order.scheduledDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div className="time-until">
                  In {getTimeUntilScheduled(order.scheduledDate)}
                </div>
              </div>

              <div className="order-items">
                {order.items?.slice(0, 2).map((item, index) => (
                  <div key={index} className="order-item">
                    • {item.name} x{item.quantity}
                  </div>
                ))}
                {order.items?.length > 2 && (
                  <div className="more-items">+{order.items.length - 2} more items</div>
                )}
              </div>

              <div className="order-footer">
                <div className="order-total">₹{order.total?.toLocaleString()}</div>
                <div className="order-actions">
                  {order.status === 'pending' && (
                    <button
                      className="btn-sm confirm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(order.id, 'confirmed');
                      }}
                    >
                      ✓ Confirm
                    </button>
                  )}
                  {order.status !== 'cancelled' && (
                    <button
                      className="btn-sm cancel"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(order.id, 'cancelled');
                      }}
                    >
                      ✗ Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Order Details #{selectedOrder.orderNumber}</h2>
              <button className="close-btn" onClick={() => setSelectedOrder(null)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h3>Customer Information</h3>
                <div className="detail-row">
                  <span>Name:</span>
                  <strong>{selectedOrder.customerName}</strong>
                </div>
                <div className="detail-row">
                  <span>Phone:</span>
                  <strong>{selectedOrder.customerPhone}</strong>
                </div>
                <div className="detail-row">
                  <span>Email:</span>
                  <strong>{selectedOrder.customerEmail || 'N/A'}</strong>
                </div>
                {selectedOrder.deliveryAddress && (
                  <div className="detail-row">
                    <span>Address:</span>
                    <strong>{selectedOrder.deliveryAddress}</strong>
                  </div>
                )}
              </div>

              <div className="detail-section">
                <h3>Schedule</h3>
                <div className="detail-row">
                  <span>Date:</span>
                  <strong>{selectedOrder.scheduledDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</strong>
                </div>
                <div className="detail-row">
                  <span>Time:</span>
                  <strong>{selectedOrder.scheduledDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong>
                </div>
                <div className="detail-row">
                  <span>Time Until:</span>
                  <strong>{getTimeUntilScheduled(selectedOrder.scheduledDate)}</strong>
                </div>
              </div>

              <div className="detail-section">
                <h3>Order Items</h3>
                <div className="items-list">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="item-row">
                      <span>{item.name} x{item.quantity}</span>
                      <strong>₹{(item.price * item.quantity).toLocaleString()}</strong>
                    </div>
                  ))}
                </div>
              </div>

              <div className="detail-section">
                <h3>Payment</h3>
                <div className="detail-row">
                  <span>Subtotal:</span>
                  <strong>₹{selectedOrder.subtotal?.toLocaleString()}</strong>
                </div>
                {selectedOrder.deliveryFee > 0 && (
                  <div className="detail-row">
                    <span>Delivery Fee:</span>
                    <strong>₹{selectedOrder.deliveryFee}</strong>
                  </div>
                )}
                {selectedOrder.discount > 0 && (
                  <div className="detail-row">
                    <span>Discount:</span>
                    <strong className="discount">-₹{selectedOrder.discount}</strong>
                  </div>
                )}
                <div className="detail-row total">
                  <span>Total:</span>
                  <strong>₹{selectedOrder.total?.toLocaleString()}</strong>
                </div>
                <div className="detail-row">
                  <span>Payment Method:</span>
                  <strong>{selectedOrder.paymentMethod || 'COD'}</strong>
                </div>
              </div>

              {selectedOrder.notes && (
                <div className="detail-section">
                  <h3>Special Instructions</h3>
                  <p>{selectedOrder.notes}</p>
                </div>
              )}

              <div className="detail-section">
                <h3>Order Status</h3>
                <div className="status-badge" style={{ backgroundColor: getStatusColor(selectedOrder.status) }}>
                  {selectedOrder.status.toUpperCase()}
                </div>
                <div className="status-actions">
                  {selectedOrder.status === 'pending' && (
                    <button
                      className="btn-primary"
                      onClick={() => handleStatusChange(selectedOrder.id, 'confirmed')}
                    >
                      ✓ Confirm Order
                    </button>
                  )}
                  {selectedOrder.status !== 'cancelled' && (
                    <>
                      <button
                        className="btn-secondary"
                        onClick={() => handleConvertToRegularOrder(selectedOrder)}
                      >
                        🔄 Place Now
                      </button>
                      <button
                        className="btn-danger"
                        onClick={() => handleStatusChange(selectedOrder.id, 'cancelled')}
                      >
                        ✗ Cancel Order
                      </button>
                    </>
                  )}
                  <button
                    className="btn-danger"
                    onClick={() => handleDeleteOrder(selectedOrder.id)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduledOrders;
